/* eslint-disable prettier/prettier */
import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/Button';
import { FileService } from '../../services/file.service';
import { CompanyService } from '../../services/company.service';
import { X, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const allFields = [
  { key: 'programNumber', label: 'Program Number' },
  { key: 'UMR', label: 'UMR' },
  { key: 'mainLineOfBusiness', label: 'Main Line of Business' },
  { key: 'subLineOfBusiness', label: 'Sub Line of Business' },
  { key: 'typeOfTreaty', label: 'Type of Treaty' },
  { key: 'businessTitle', label: 'Business Title' },
  { key: 'underwritingYear', label: 'Underwriting Year' },
  { key: 'inceptionDate', label: 'Inception Date' },
  { key: 'expiryDate', label: 'Expiry Date' },
  { key: 'multiYear', label: 'Multi Year' },
  { key: 'contractDuration', label: 'Contract Duration' },
  { key: 'mainTerritorialScope', label: 'Main Territorial Scope' },
  { key: 'contractCurrency', label: 'Contract Currency' },
  { key: 'claimBasis', label: 'Claim Basis' },
  { key: 'accountingBasis', label: 'Accounting Basis' },
  { key: 'brokerageRate', label: 'Brokerage Rate' },
  { key: 'taxesRate', label: 'Taxes Rate' },
  { key: 'cedant', label: 'Cedant' },
  { key: 'broker', label: 'Broker' },
  { key: 'country', label: 'Country' },
];

export const ComparePPPage = () => {
  const [programFields, setProgramFields] = useState<string[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [usedUnderwritingYear, setUsedUnderwritingYear] = useState(false);

  useEffect(() => {
    const fetchDefinitionAndSearch = async () => {
      try {
        const defRes = await CompanyService.getProgramDefinition();
        const fields = defRes.data || [];
        setProgramFields(fields);

        if (fields.length > 0) {
          setUsedUnderwritingYear(fields.includes('underwritingYear'));
          const res = await FileService.searchComparePP({ fields });
          setResults(res.data || []);
        }
      } catch (e) {
        console.error('Error fetching program or search results:', e);
      }
    };

    fetchDefinitionAndSearch();
  }, []);

  const cleanGroupName = (raw: string) =>
    raw
      .split('|')
      .map((part) => part.split(':')[1] || '')
      .join(' | ');

  const displayFieldLabels = (keys: string[]) => {
    return keys
      .map((key) => allFields.find((f) => f.key === key)?.label || key)
      .join(', ');
  };

  return (
    <div className="overflow-hidden">
      <h1 className="mb-6 text-4xl font-bold text-center sm:text-left text-primary">
        Compare Programs
      </h1>

      <div className="mb-6">
        {programFields.length > 0 ? (
          <p className="text-base">
            <span className="font-semibold text-primary">Program is defined on:</span>{' '}
            {displayFieldLabels(programFields)}
          </p>
        ) : (
          <p className="font-medium text-red-600">
            No program definition found. Please define the program or contact your company admin.
          </p>
        )}
      </div>

      <div>
        {results.length > 0 ? (
          <div className="space-y-4">
            {results.map((group, idx) => (
              <div key={idx} className="p-4 border rounded bg-gray-50">
                <div
                  className="font-semibold cursor-pointer text-primary hover:underline"
                  onClick={() =>
                    setExpandedGroup((prev) => (prev === group.groupName ? null : group.groupName))
                  }
                >
                  {cleanGroupName(group.groupName)}
                </div>

                {expandedGroup === group.groupName && (
                  <>
                    {usedUnderwritingYear ? (
                      <ul className="pl-5 mt-2 list-disc">
                        {group.treaties.map((t: any) => (
                          <li key={t.id}>
                            <Link
                              to={`/files/${t.fileId}`}
                              className="text-primary hover:underline"
                            >
                              {t.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <table className="w-full mt-4 text-left border border-collapse border-gray-300 table-auto">
                        <thead>
                          <tr className="bg-gray-100">
                            {Object.keys(
                              group.treaties.reduce((acc: Record<string, any[]>, curr: any) => {
                                const year = curr.underwritingYear || 'Unknown';
                                if (!acc[year]) acc[year] = [];
                                acc[year].push(curr);
                                return acc;
                              }, {})
                            )
                              .sort()
                              .map((year) => (
                                <th key={year} className="p-2 border">
                                  {year}
                                </th>
                              ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const grouped = group.treaties.reduce(
                              (acc: Record<string, any[]>, curr: any) => {
                                const year = curr.underwritingYear || 'Unknown';
                                if (!acc[year]) acc[year] = [];
                                acc[year].push(curr);
                                return acc;
                              },
                              {}
                            );

                            const years = Object.keys(grouped).sort();
                            const maxRows = Math.max(
                              ...Object.values(grouped).map((list) => (list as any[]).length)
                            );

                            return Array.from({ length: maxRows }).map((_, rowIndex) => (
                              <tr key={rowIndex}>
                                {years.map((year) => (
                                  <td key={year} className="p-2 align-top border">
                                    {grouped[year][rowIndex] ? (
                                      <Link
                                        to={`/files/${grouped[year][rowIndex].fileId}`}
                                        className="block text-primary hover:underline"
                                      >
                                        {grouped[year][rowIndex].name}
                                      </Link>
                                    ) : (
                                      ''
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        ) : programFields.length > 0 && !loading ? (
          <p className="text-muted-foreground">No results found.</p>
        ) : null}
      </div>
    </div>
  );
};
